import { useEffect, useMemo, useRef, useState } from 'react';

import { createFamilyEvent } from '@src/api/event/createFamilyEvent';
import { GetMemberResponse, getMember } from '@src/api/member/getMember';
import { validMessage } from '@src/constants/message';
import { useIsMobile } from '@src/hooks/useIsMobile';
import { closeModal } from '@src/reduce/modals';
import { trigger } from '@src/utils/globalEvents';
import { Button, Radio, message } from 'antd';
import { useDispatch } from 'react-redux';

import { openModal } from '@reduce/modals';

import { updateFamilyEvent, updateFamilyEventApprove } from '@api/event';

import getIsResponseFalse from '@utils/getIsResponseFalse';

import FormFuneral from './FormFuneral';
import FormOpening from './FormOpening';
import FormWedding from './FormWedding';
import FuneralPreview from './preview/funeral';
import OpeningPreview from './preview/opening';
import WeddingPreview from './preview/wedding';
import * as S from './styled';
import * as T from './type';

const options = [
  { label: '결혼', value: 'wedding' },
  { label: '부고', value: 'funeral' },
  { label: '개업', value: 'opening' },
];

const getFamilyEventFullTitle = ({
  customTitle,
  targetType,
  type,
}: {
  customTitle?: string;
  type: 'wedding' | 'funeral' | 'opening';
  targetType: string;
}) => {
  if (!customTitle || type === 'opening') return undefined;

  if (type === 'funeral') {
    return `[訃音] ${customTitle} ${targetType}`;
  }

  return `[결혼] ${customTitle} ${targetType} 결혼 알림`;
};

interface CreateFamilyEventModalProps {
  initialFormData?: T.CreateFamilyEvent & { type: T.FamilyEventType; id: number };
  requestBySelf?: boolean;
  isEditable?: boolean;
  approveStatus: 'pending' | 'approved' | 'rejected';
  initialFileName?: string;
}
const CreateFamilyEventModal = ({
  initialFormData,
  requestBySelf,
  isEditable,
  approveStatus,
  initialFileName,
}: CreateFamilyEventModalProps) => {
  const dispatch = useDispatch();

  const [familyEventType, setFamilyEventType] = useState<T.FamilyEventType>(
    initialFormData?.type || 'wedding',
  );
  const [isEditMode, setIsEditMode] = useState<boolean>(!!isEditable);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [licenseNosMember, setLicenseNosMember] = useState<GetMemberResponse[]>([]);
  const [formData, setFormData] = useState<T.CreateFamilyEvent>({
    licenseNos: initialFormData?.licenseNos || [],
    targetType: initialFormData?.targetType || (familyEventType === 'opening' ? '개업' : ''),
    date: initialFormData?.date || '',
    address: initialFormData?.address || '',
    detailAddress: initialFormData?.detailAddress || '',
    contacts: initialFormData?.contacts || [],
    paymentAccount: initialFormData?.paymentAccount || '',
    targetName: initialFormData?.targetName || '',
    actionType: initialFormData?.actionType || [],
    funeralDate: initialFormData?.funeralDate || undefined,
    remarks: initialFormData?.remarks || undefined,
    customTitle: initialFormData?.customTitle,
    link: initialFormData?.link,
  });

  useEffect(() => {
    getLicenseNosMember();
  }, [initialFormData?.licenseNos]);

  const firstRenderRef = useRef(true);
  useEffect(() => {
    setFormData({
      licenseNos: initialFormData?.licenseNos || [],
      targetType: initialFormData?.targetType || (familyEventType === 'opening' ? '개업' : ''),
      date: initialFormData?.date || '',
      address: initialFormData?.address || '',
      detailAddress: initialFormData?.detailAddress || '',
      contacts: initialFormData?.contacts || [],
      paymentAccount: initialFormData?.paymentAccount || '',
      targetName: initialFormData?.targetName || '',
      actionType: initialFormData?.actionType || [],
      funeralDate: initialFormData?.funeralDate || undefined,
      remarks: initialFormData?.remarks || undefined,
      customTitle: initialFormData?.customTitle,
      link: initialFormData?.link,
    });

    if (!firstRenderRef.current) {
      setTitle('');
    } else {
      firstRenderRef.current = false;
    }
  }, [initialFormData, familyEventType]);

  useEffect(() => {
    setIsEditMode(!!isEditable);
  }, [isEditable, initialFormData?.id]);

  const getLicenseNosMember = async () => {
    if (formData.licenseNos.length === 0) return;

    const response = await Promise.all(
      formData.licenseNos.map(licenseNo => {
        return getMember({ path: { licenseNo } });
      }),
    );

    if (response.includes(false)) return;

    setLicenseNosMember(response as GetMemberResponse[]);
  };

  const validationCheck = () => {
    const {
      licenseNos,
      targetType,
      date,
      address,
      detailAddress,
      remarks,
      targetName,
      actionType,
      link,
      fileIds,
    } = formData;

    if (licenseNos.length === 0) {
      message.error(validMessage[familyEventType].licenseNos);
      return false;
    } else if (!targetType && familyEventType !== 'opening') {
      message.error(validMessage[familyEventType].targetType);
      return false;
    } else if (!date) {
      message.error(validMessage[familyEventType].date);
      return false;
    } else if (!address) {
      message.error(validMessage[familyEventType].address);
      return false;
    } else if (!detailAddress) {
      message.error(validMessage[familyEventType].detailAddress);
      return false;
    } else if (!remarks && familyEventType === 'funeral') {
      message.error(validMessage[familyEventType].remarks);
      return false;
    } else if (!targetName && familyEventType !== 'wedding') {
      message.error(validMessage[familyEventType].targetName);
      return false;
    } else if (actionType.length === 0) {
      message.error(validMessage[familyEventType].actionType);
      return false;
    } else if (actionType.includes('sms') && !actionType.includes('homepage')) {
      message.error('"문자 발송" 선택 시 "홈페이지 게시"도 같이 선택해주셔야 합니다.');
      return false;
    } else if (link && !/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i.test(link)) {
      message.error('링크는 URL 형식만 입력 가능합니다.');
      return false;
    } else if (link && fileIds && fileIds.length) {
      message.error('링크와 첨부파일 중 하나만 입력할 수 있습니다.');
      return false;
    }

    return true;
  };

  const createEvent = async () => {
    if (!validationCheck()) return;

    setIsLoading(true);

    const customTitle = getFamilyEventFullTitle({
      customTitle: formData.customTitle,
      targetType: formData.targetType,
      type: familyEventType,
    });

    const response = await createFamilyEvent({
      data: { ...formData, customTitle, type: familyEventType },
    });

    setIsLoading(false);
    if (!response) return;

    message.success('경조사가 등록되었습니다.');
    trigger('SUCCESS_UPDATE_FAMILY_EVENT');
    close();
  };

  const close = () => {
    dispatch(closeModal('createFamilyEventModal'));
  };

  const edit = async (isResend?: boolean) => {
    if (!initialFormData?.id) return;
    if (!validationCheck()) return;

    setIsLoading(true);

    const customTitle = getFamilyEventFullTitle({
      customTitle: formData.customTitle,
      targetType: formData.targetType,
      type: familyEventType,
    });

    const response = await updateFamilyEvent({
      path: { eventId: initialFormData.id },
      data: { ...formData, customTitle, type: familyEventType, isResend },
    });

    setIsLoading(false);
    if (getIsResponseFalse(response)) return;

    message.success('수정 되었습니다.');
    trigger('SUCCESS_UPDATE_FAMILY_EVENT');
    close();
  };

  const approved = async () => {
    if (!initialFormData?.id) return;

    setIsLoading(true);

    const response = await updateFamilyEventApprove({
      path: { eventId: initialFormData.id },
      data: { approved: true },
    });

    setIsLoading(false);
    if (getIsResponseFalse(response)) return;

    message.success('승인 되었습니다.');
    trigger('SUCCESS_UPDATE_FAMILY_EVENT');
    setShowConfirmModal(false);
    close();
  };

  const rejected = async () => {
    if (!initialFormData?.id) return;

    dispatch(openModal({ name: 'rejectedFamilyEvent', props: { id: initialFormData.id } }));
  };

  const modalTitle = useMemo(() => {
    return isEditMode
      ? '경조사 수정'
      : initialFormData
      ? '상세보기'
      : isPreviewMode
      ? '경조사 미리보기'
      : '경조사 등록';
  }, [initialFormData, isPreviewMode, isEditMode]);

  const footerBtns = (() => {
    if (initialFormData) {
      if (isEditMode)
        return [
          <Button key='cancel' onClick={() => close()}>
            취소
          </Button>,
          <Button
            key='edit'
            type='primary'
            loading={isLoading}
            onClick={() => (approveStatus === 'approved' ? setShowConfirmModal(true) : edit())}>
            저장
          </Button>,
        ];

      // (!requestBySelf || initialFormData.type === 'wedding') &&
      if (approveStatus === 'pending')
        return [
          <Button
            key='cancel'
            onClick={() => {
              setIsPreviewMode(prev => !prev);
            }}>
            {isPreviewMode ? '뒤로가기' : '미리보기'}
          </Button>,
          <Button
            key='edit'
            onClick={() => {
              setIsPreviewMode(false);
              setIsEditMode(true);
            }}>
            수정
          </Button>,
          <Button key='cancel' onClick={() => rejected()}>
            반려
          </Button>,
          <Button key='approve' type='primary' loading={isLoading} onClick={() => approved()}>
            승인
          </Button>,
        ];

      return (
        <Button key='close' type='primary' onClick={() => close()}>
          확인
        </Button>
      );
    }

    return [
      <Button
        key='cancel'
        onClick={() => {
          setIsPreviewMode(prev => !prev);
        }}>
        {isPreviewMode ? '수정' : '미리보기'}
      </Button>,
      <Button key='send' type='primary' loading={isLoading} onClick={createEvent}>
        등록
      </Button>,
    ];
  })();

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isMobile = useIsMobile();

  return (
    <>
      <S.ModalContainer
        isConfirm
        centered
        open={showConfirmModal}
        width='440px'
        closable={false}
        footer={[
          <Button key='cancel' loading={isLoading} onClick={() => edit(false)}>
            아니요
          </Button>,
          <Button key='reSend' type='primary' loading={isLoading} onClick={() => edit(true)}>
            재발송
          </Button>,
        ]}>
        <S.Container style={{ display: 'flex', height: '100%', alignItems: 'center' }}>
          <p>경조사가 수정이 되었습니다. 알림메시지를 재발송하시겠습니까?</p>
        </S.Container>
      </S.ModalContainer>
      <S.ModalContainer
        isConfirm={isMobile}
        open={true}
        title={modalTitle}
        footer={footerBtns}
        width='800px'
        onCancel={close}>
        <S.Container>
          {isPreviewMode ? (
            <>
              {(() => {
                switch (familyEventType) {
                  case 'wedding':
                    return <WeddingPreview formData={formData} title={title} />;
                  case 'funeral':
                    return <FuneralPreview formData={formData} title={title} />;
                  case 'opening':
                    return <OpeningPreview formData={formData} title={title} />;
                  default:
                    return <></>;
                }
              })()}
            </>
          ) : (
            <>
              <S.radioWrapper>
                <p>경조사 구분</p>
                <Radio.Group
                  options={options}
                  value={familyEventType}
                  disabled={!!initialFormData}
                  onChange={({ target: { value } }) => setFamilyEventType(value)}
                />
              </S.radioWrapper>

              {(() => {
                switch (familyEventType) {
                  case 'wedding':
                    return (
                      <FormWedding
                        formData={formData}
                        setFormData={setFormData}
                        setTitle={setTitle}
                        hasInitialFormData={!!initialFormData}
                        isEditable={isEditMode}
                        initialFileName={initialFileName}
                        approveStatus={approveStatus}
                        licenseNosMember={licenseNosMember}
                      />
                    );
                  case 'funeral': {
                    return (
                      <FormFuneral
                        formData={formData}
                        setFormData={setFormData}
                        setTitle={setTitle}
                        hasInitialFormData={!!initialFormData}
                        isEditable={isEditMode}
                        initialFileName={initialFileName}
                        approveStatus={approveStatus}
                        licenseNosMember={licenseNosMember}
                      />
                    );
                  }
                  case 'opening':
                    return (
                      <FormOpening
                        formData={formData}
                        setFormData={setFormData}
                        setTitle={setTitle}
                        hasInitialFormData={!!initialFormData}
                        isEditable={isEditMode}
                        initialFileName={initialFileName}
                        approveStatus={approveStatus}
                        licenseNosMember={licenseNosMember}
                      />
                    );
                  default:
                    return <></>;
                }
              })()}
            </>
          )}
          {/* <DatePicker showTime allowClear={false} format='YYYY-MM-DD HH:mm' /> */}
        </S.Container>
      </S.ModalContainer>
    </>
  );
};

export default CreateFamilyEventModal;
